/**
 * Pre-generation pickers — a lightweight CLIENT mirror of the backend media router
 * (functions/src/agents/intent/mediaRouter) used only to decide whether to OFFER the user a
 * dimension picker (image/video) or a singer panel (music) before they send. The backend
 * stays the source of truth for what actually runs; this just surfaces the right controls.
 *
 * The chosen aspect ('9:16' | '4:5' | '1:1' | '16:9') maps to a social-platform shape, and the
 * music choice is folded into a natural-language `musicStyle` descriptor prepended to the prompt.
 */
export type MediaIntent = 'image' | 'video' | 'music' | null;

const MUSIC_RE = /\b(song|music|track|tune|melody|jingle|instrumental|compose|theme song|beat|lyrics?|sing)\b/;
const VIDEO_RE = /\b(video|animat\w*|clip|reel|short film|motion|footage|movie|trailer|b[- ]?roll)\b/;
const IMAGE_GEN_RE = /\b(generate|create|draw|make|design|render|illustrat\w*|paint|visuali[sz]e)\b/;
const IMAGE_NOUN_RE = /\b(image|picture|photo|illustration|graphic|artwork|poster|logo|drawing|render|wallpaper|icon|avatar|banner)\b/;
const IMAGE_OF_RE = /\b(image|picture|photo|logo|poster|illustration|artwork|banner) of\b/;

/** Which media picker (if any) fits the current draft. Music wins over video over image. */
export function detectMediaIntent(text: string): MediaIntent {
  const t = (text || '').toLowerCase();
  if (!t.trim()) return null;
  if (MUSIC_RE.test(t)) return 'music';
  if (VIDEO_RE.test(t)) return 'video';
  if (IMAGE_OF_RE.test(t)) return 'image';
  if (IMAGE_GEN_RE.test(t) && IMAGE_NOUN_RE.test(t)) return 'image';
  return null;
}

// ── Dimension picker (image + video) ─────────────────────────────────────────
export interface AspectOption { value: string; label: string; platforms: string }
export const ASPECT_OPTIONS: AspectOption[] = [
  { value: '9:16', label: 'Portrait', platforms: 'TikTok · Reels · Shorts · Stories' },
  { value: '4:5', label: 'Feed', platforms: 'Instagram feed' },
  { value: '1:1', label: 'Square', platforms: 'Profile · thumbnail' },
  { value: '16:9', label: 'Landscape', platforms: 'YouTube · presentation' },
];
export const DEFAULT_ASPECT = '1:1';

// ── Output size (resolution/quality) — image + video. Standard is the fast default; HD renders at a
//    higher resolution. Threaded through as `mediaSize`; the backend maps it to the model's params.
export interface SizeOption { value: string; label: string; hint: string }
export const SIZE_OPTIONS: SizeOption[] = [
  { value: 'standard', label: 'Standard', hint: 'Fast, everyday resolution' },
  { value: 'hd', label: 'HD', hint: 'Higher resolution, more detail' },
];
export const DEFAULT_SIZE = 'standard';

// ── Style presets (prompt factory) — keys MUST match the server (agents/mediaStyles.ts). The
//    server owns the actual prompt fragment; the client only sends the key. Pick a look without typing.
export interface StyleOption { key: string; label: string }
export const STYLE_PRESETS: Record<'image' | 'video', StyleOption[]> = {
  image: [
    { key: 'photoreal', label: 'Photoreal' }, { key: 'portrait', label: 'Portrait' },
    { key: 'illustration', label: 'Illustration' }, { key: 'cinematic', label: 'Cinematic' },
    { key: 'watercolor', label: 'Watercolour' }, { key: 'render3d', label: '3D' },
    { key: 'lineart', label: 'Line art' }, { key: 'anime', label: 'Anime' },
  ],
  video: [
    { key: 'cinematic', label: 'Cinematic' }, { key: 'animation', label: 'Animated' },
    { key: 'timelapse', label: 'Timelapse' }, { key: 'documentary', label: 'Documentary' },
  ],
};

// ── Camera moves (video only) — named cinematographer moves; keys MUST match the server
//    (agents/mediaStyles.ts CAMERA_MOVES). Click a move, never keyframes. Server owns the prompt fragment.
export const CAMERA_MOVES: StyleOption[] = [
  { key: 'push_in', label: 'Push in' }, { key: 'pull_out', label: 'Pull out' },
  { key: 'orbit', label: '360 Orbit' }, { key: 'crane_up', label: 'Crane up' },
  { key: 'crash_zoom', label: 'Crash zoom' }, { key: 'fpv_drone', label: 'FPV drone' },
  { key: 'whip_pan', label: 'Whip pan' }, { key: 'rack_focus', label: 'Rack focus' },
  { key: 'handheld', label: 'Handheld' },
];

// ── Singer panel (music) ─────────────────────────────────────────────────────
// The TYPE routes to a different model behind an abstract capability (never a model name):
//   Song        → vocals model (`music` capability)          — ~1 min, warm & sync
//   Instrumental→ high-quality instrumental model (`music_instrumental`) — the good instrumental tier
//   Sound FX    → short sound-design model (`sfx`)          — a few seconds, one unit
export interface MusicKindOption { key: 'music' | 'music_instrumental' | 'sfx'; label: string; hint: string }
export const MUSIC_KINDS: MusicKindOption[] = [
  { key: 'music', label: 'Song', hint: 'A full song with vocals from your lyrics' },
  { key: 'music_instrumental', label: 'Instrumental', hint: 'A rich instrumental track, no vocals' },
  { key: 'sfx', label: 'Sound FX', hint: 'A short sound effect or ambience' },
];
export const DEFAULT_MUSIC_KIND = 'music';

export const SINGER_VOICES = ['Female', 'Male', 'Duet'] as const;
export const VOICE_STYLES = ['Smooth', 'Powerful', 'Soft', 'Raspy', 'Bright'] as const;
export const MUSIC_GENRES = ['Pop', 'Lo-fi', 'R&B', 'Rock', 'EDM', 'Acoustic', 'Cinematic', 'Hip-hop'] as const;

/** Song lengths (seconds) the user can pick — 30 s up to 2 m 30 s. Longer costs more (per 30 s). */
export const SONG_LENGTHS = [30, 60, 90, 120, 150] as const;
export const DEFAULT_SONG_LENGTH = 30;
/** Format seconds as m:ss for the length chips (30 → 0:30, 150 → 2:30). */
export const formatSongLength = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

export interface MusicChoice { kind?: 'music' | 'music_instrumental' | 'sfx'; voice?: string; voiceStyle?: string; genre?: string; durationSec?: number }

/** The capability the chosen TYPE routes to (validated again server-side). Defaults to a vocal song. */
export function buildMusicKind(c: MusicChoice): string {
  return c.kind || DEFAULT_MUSIC_KIND;
}

/** Fold the singer panel choices into a short descriptor prepended to the user's music prompt. */
export function buildMusicStyle(c: MusicChoice): string {
  const kind = c.kind || DEFAULT_MUSIC_KIND;
  if (kind === 'sfx') return '';                                   // the prompt itself IS the sound description
  const parts: string[] = [];
  if (c.genre) parts.push(c.genre);
  if (kind === 'music_instrumental') { parts.push('instrumental, no vocals'); return parts.join(', '); }
  if (c.voice) parts.push(`${c.voice.toLowerCase()} vocals`);
  if (c.voiceStyle) parts.push(`${c.voiceStyle.toLowerCase()} voice`);
  return parts.join(', ');
}
